import { useEffect, useState } from 'react';

/**
 * 複数タブ検知 hook（docs/03 §7.5-3 「複数タブ書込安全性の方針」 **層2 = UI 誘導**, 2026-06-06）。
 *
 * 背景:
 *   Firestore の永続キャッシュは `persistentSingleTabManager({ forceOwnership: true })` 固定（#7341
 *   postmortem 以降）。forceOwnership は「最新に開いたタブが IndexedDB の所有権 (lease) を無条件で奪う」
 *   挙動で、奪われた **古いタブはメモリキャッシュへ縮退**する。縮退した古いタブは
 *   サーバ更新をサイレントに取りこぼし「投稿したのに反映されない」(MT-2) が起き得る。
 *
 * 本 hook の役割（層2・保険）:
 *   - 同時に開いているタブ数を検知する
 *   - **どのタブが最新 (= Firestore の所有権を持つ owner) か**を判定する。forceOwnership と同じく
 *     「最も後に開いたタブ = owner」とし、それ以外の古いタブを「非 owner」とする
 *   - 非 owner タブだけに非ブロッキングの警告を出す材料 (`showWarning`) を返す。
 *     **書込のハードブロックはしない**（層1 = 冪等 outbox で書込自体は安全なため）。
 *
 * 検知方式（Phase 1）:
 *   BroadcastChannel によるハートビート。各タブが一意 ID + 起動時刻 (openedAt) を周期 broadcast し、
 *   受信側がタブ表を保持・期限切れを掃除する。Web Locks API は Phase 2（層1 = flush 直列化）で導入予定で、
 *   Phase 1 の「検知 + ソフト警告」には不要（secure context 依存も避ける）。
 *   BroadcastChannel 非対応ブラウザでは単一タブとして振る舞う（警告を出さない）。
 *
 * 既存の outbox.ts / ecwOutbox.ts と同じ BroadcastChannel パターンに揃えている。
 */

/** ハートビート送出間隔 (ms)。 */
const HEARTBEAT_MS = 2000;
/** この時間ハートビートが来ないタブは閉じた/落ちたとみなして掃除する (ms)。missed 2 回まで許容。 */
const STALE_MS = 6000;
/** タブ間通信チャネル名 (posts/outbox の 'citizen-walk-outbox-changed' と同 namespace)。 */
const CHANNEL_NAME = 'citizen-walk-tabs';

export interface MultiTabState {
  /** 同時に開いている (生存) タブ数。自タブを含む。非対応環境では常に 1。 */
  tabCount: number;
  /** このタブが最新 (= Firestore lease の所有タブ、警告を出さない側) か。 */
  isOwner: boolean;
  /** 複数タブが開いており、かつこのタブが非 owner (古い) → ソフト警告を出すべきか。 */
  showWarning: boolean;
}

const SINGLE_TAB_STATE: MultiTabState = { tabCount: 1, isOwner: true, showWarning: false };

interface TabBeat {
  /** 相手タブの起動時刻 (ms epoch)。新しいほど owner に近い。 */
  openedAt: number;
  /** 最後にハートビートを受けた時刻 (ms epoch)。STALE 判定に使う。 */
  lastSeen: number;
}

type TabMessage =
  | { type: 'hello'; tabId: string; openedAt: number }
  | { type: 'beat'; tabId: string; openedAt: number }
  | { type: 'bye'; tabId: string };

function generateTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* secure context 外で randomUUID が無いブラウザ向け fallback */
  }
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useMultiTab(): MultiTabState {
  const [state, setState] = useState<MultiTabState>(SINGLE_TAB_STATE);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      // 非対応環境 (古い browser / SSR / test) は単一タブとして扱う。
      return;
    }

    const tabId = generateTabId();
    const openedAt = Date.now();
    const others = new Map<string, TabBeat>();
    const channel = new BroadcastChannel(CHANNEL_NAME);

    const recompute = () => {
      const now = Date.now();
      for (const [id, beat] of others) {
        if (now - beat.lastSeen > STALE_MS) others.delete(id);
      }
      const tabCount = others.size + 1;
      // forceOwnership と同じ「最も後に開いたタブ = owner」。openedAt 同値は tabId の辞書順で決定。
      let isOwner = true;
      for (const [id, beat] of others) {
        if (beat.openedAt > openedAt || (beat.openedAt === openedAt && id > tabId)) {
          isOwner = false;
          break;
        }
      }
      const showWarning = tabCount >= 2 && !isOwner;
      setState((prev) =>
        prev.tabCount === tabCount && prev.isOwner === isOwner && prev.showWarning === showWarning
          ? prev
          : { tabCount, isOwner, showWarning },
      );
    };

    const post = (msg: TabMessage) => {
      try {
        channel.postMessage(msg);
      } catch {
        /* チャネルが閉じた直後など。致命的ではない */
      }
    };
    const sendBeat = () => post({ type: 'beat', tabId, openedAt });

    const handleMessage = (event: MessageEvent) => {
      const msg = event.data as TabMessage | undefined;
      if (
        !msg ||
        typeof msg !== 'object' ||
        typeof (msg as { tabId?: unknown }).tabId !== 'string'
      ) {
        return;
      }
      if (msg.tabId === tabId) return; // 自タブ (BroadcastChannel は通常自タブに配信しないが念のため)
      if (msg.type === 'bye') {
        if (others.delete(msg.tabId)) recompute();
        return;
      }
      if ((msg.type === 'hello' || msg.type === 'beat') && typeof msg.openedAt === 'number') {
        others.set(msg.tabId, { openedAt: msg.openedAt, lastSeen: Date.now() });
        recompute();
        // 新規タブの 'hello' には即 beat を返し、相手がこちらを取りこぼさないようにする。
        if (msg.type === 'hello') sendBeat();
      }
    };

    channel.addEventListener('message', handleMessage);

    // 起動を周知し、既存タブにも自分を周知させる。
    post({ type: 'hello', tabId, openedAt });

    const interval = window.setInterval(() => {
      sendBeat();
      recompute();
    }, HEARTBEAT_MS);

    const handleBye = () => post({ type: 'bye', tabId });
    // pagehide は bfcache/タブ閉じ両対応 (unload は bfcache を壊すため使わない)。
    window.addEventListener('pagehide', handleBye);

    return () => {
      handleBye();
      window.clearInterval(interval);
      window.removeEventListener('pagehide', handleBye);
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, []);

  return state;
}
